import { animationTheme } from '../../components/Animation/animation.theme';
import { buttonTheme } from '../../components/Button/button.theme';
import { buttonGroupTheme } from '../../components/ButtonGroup/buttonGroup.theme';
import { cardTheme } from '../../components/Card/card.theme';
import { containerTheme } from '../../components/Layout/Container/container.theme';
import { formTheme } from '../../components/Form/form.theme';
import { linkTheme } from '../../components/Link/link.theme';
import { loadingSpinnerTheme } from '../../components/LoadingSpinner/loadingSpinner.theme';
import { sectionTheme } from '../../components/Layout/Section/section.theme';
import { heroTheme } from '../../components/Layout/Hero/hero.theme';
import { sliderTheme } from '../../components/Slider/slider.theme';
import { alertTheme } from '../../components/Alert/alert.theme';
import { avatarTheme } from '../../components/Avatar/avatar.theme';
import { iconTheme } from '../../components/Icon/icon.theme';
import { modalTheme } from '../../components/Modal/modal.theme';
import { ratingTheme } from '../../components/Rating/rating.theme';
import { tooltipTheme } from '../../components/Tooltip/tooltip.theme';
import { notificationsTheme } from '../../components/Notifications/notifications.theme';
import { tableTheme } from '../../components/Table/table.theme';
import { dropdownTheme } from '../../components/DropDown/dropdown.theme';
import { stepperTheme } from '../../components/Stepper/stepper.theme';
import { colors } from './colors';
import { toolbarTheme } from '../../components/Toolbar/Toolbar.theme';
import { appBarTheme } from '../../components/AppBar/AppBar.theme';

const globalStyles = {
  '*, *::before, *::after': {
    margin: 0,
    padding: 0,
    border: 0,
    boxSizing: 'border-box',
    listStyle: 'none',
    textDecoration: 'none',
    fontFamily: 'Titillium Web, Open Sans',
    MozOsxFontSmoothing: 'grayscale',
  },
  body: {
    fontFamily: 'Titillium Web, Open Sans',
    fontSize: '16px',
    backgroundColor: 'inherit',
    color: (theme: MapUiTheme) => theme.colors.black,
    fontWeight: (theme: MapUiTheme) => theme.fontWeight.light,
    lineHeight: (theme: MapUiTheme) => theme.lineHeight.normal,
    h1: {
      color: (theme: MapUiTheme) => theme.colors.text300,
      fontWeight: (theme: MapUiTheme) => theme.fontWeight.semibold,
      fontSize: (theme: MapUiTheme) => theme.fontSize['5xl'],
      margin: 'initial',
    },
    h2: {
      fontSize: (theme: MapUiTheme) => theme.fontSize['3xl'],
      fontWeight: (theme: MapUiTheme) => theme.fontWeight.semibold,
      margin: 'initial',
    },
    h3: {
      fontSize: (theme: MapUiTheme) => theme.fontSize.xl,
      fontWeight: (theme: MapUiTheme) => theme.fontWeight.semibold,
      margin: 'initial',
    },
    p: {
      fontSize: (theme: MapUiTheme) => theme.fontSize.base,
      margin: 'initial',
    },
    a: {
      cursor: 'pointer',
    },
  },
};

const screensStyles = {
  sm: '30em',
  md: '48em',
  lg: '62em',
  xl: '80em',
  '2xl': '96em',
};

const spacingStyles = {
  px: '1px',
  0: '0',
  1: '0.32rem',
  2: '0.5rem',
  3: '0.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  11: '2.75rem',
  12: '3rem',
  13: '3.25rem',
  14: '3.5rem',
  15: '3.75rem',
  16: '4rem',
  20: '5rem',
  24: '6rem',
  32: '8rem',
  40: '10rem',
  48: '12rem',
  56: '14rem',
  64: '16rem',
};

const zIndicesStyles = {
  hide: -1,
  auto: 'auto',
  base: 0,
  docked: 10,
  dropdown: 1000,
  sticky: 1100,
  banner: 1200,
  overlay: 1300,
  modal: 1400,
  popover: 1500,
  skipLink: 1600,
  toast: 1700,
  tooltip: 1800,
};

const borderRadiusStyles = {
  none: '0',
  sm: '2px',
  default: '4px',
  md: '6px',
  lg: '8px',
  full: '9999px',
};

const boxShadowStyles = {
  xs: '0 0 0 1px rgba(0, 0, 0, 0.05)',
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  default: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  '2xl': '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)',
  outline: '0 0 0 3px rgba(66, 153, 225, 0.5)',
  none: 'none',
};

const fontSizeStyles = {
  // 12px con 1rem = 16px
  xs: '0.75rem',
  // 14px con 1rem = 16px
  sm: '0.875rem',
  // 16px con 1rem = 16px
  base: '1rem',
  // 18px con 1rem = 16px
  lg: '1.125rem',
  // 22px con 1rem = 16px
  xl: '1.375rem',
  // 24px con 1rem = 16px
  '2xl': '1.5rem',
  // 30px con 1rem = 16px
  '3xl': '1.875rem',
  // 36px con 1rem = 16px
  '4xl': '2.25rem',
  // 44px con 1rem = 16px
  '5xl': '2.75rem',
  // 48px con 1rem = 16px
  '6xl': '3rem',
};

const fontWeightStyles = {
  hairline: '100',
  thin: '200',
  light: '300',
  normal: '400',
  medium: '500',
  semibold: '600',
  bold: '700',
  extrabold: '800',
  black: '900',
};

const letterSpacingStyles = {
  tighter: '-0.05em',
  tight: '-0.025em',
  normal: '0',
  wide: '0.025em',
  wider: '0.05em',
  widest: '0.1em',
};

const lineHeightStyles = {
  none: '1rem',
  tight: '1.25rem',
  snug: '1.063rem',
  select: '1.2rem',
  normal: 1.52,
  relaxed: '1.625rem',
  loose: '2rem',
  extraLoose: '6rem',
  3: '.75rem',
  4: '1rem',
  5: '1.25rem',
  6: '1.5rem',
  7: '1.75rem',
  8: '2rem',
  9: '2.25rem',
  10: '2.5rem',
  12: '3rem',
};

const animationStyles = {
  none: 'none',
  spin: 'spin 1s linear infinite',
  ping: 'ping 1s cubic-bezier(0, 0, 0.2, 1) infinite',
  pulse: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
  bounce: 'bounce 1s infinite',
};

const keyframesStyles = {
  spin: {
    to: { transform: 'rotate(360deg)' },
  },
  ping: {
    '75%, 100%': { transform: 'scale(2)', opacity: '0' },
  },
  pulse: {
    '50%': { opacity: '.5' },
  },
  bounce: {
    '0%, 100%': {
      transform: 'translateY(-25%)',
      animationTimingFunction: 'cubic-bezier(0.8,0,1,1)',
    },
    '50%': {
      transform: 'none',
      animationTimingFunction: 'cubic-bezier(0,0,0.2,1)',
    },
  },
};

export interface MapUiTheme {
  global: typeof globalStyles;
  screens: typeof screensStyles;
  spacing: typeof spacingStyles;
  zIndices: typeof zIndicesStyles;
  borderRadius: typeof borderRadiusStyles;
  boxShadow: typeof boxShadowStyles;
  fontSize: typeof fontSizeStyles;
  fontWeight: typeof fontWeightStyles;
  letterSpacing: typeof letterSpacingStyles;
  lineHeight: typeof lineHeightStyles;
  animation: typeof animationStyles;
  keyframes: typeof keyframesStyles;
  hero: typeof heroTheme;
  colors: typeof colors;
  alert: typeof alertTheme;
  animations: typeof animationTheme;
  avatar: typeof avatarTheme;
  button: typeof buttonTheme;
  buttonGroup: typeof buttonGroupTheme;
  card: typeof cardTheme;
  container: typeof containerTheme;
  dropdown: typeof dropdownTheme;
  form: typeof formTheme;
  icon: typeof iconTheme;
  link: typeof linkTheme;
  loadingSpinner: typeof loadingSpinnerTheme;
  modal: typeof modalTheme;
  notifications: typeof notificationsTheme;
  section: typeof sectionTheme;
  slider: typeof sliderTheme;
  rating: typeof ratingTheme;
  stepper: typeof stepperTheme;
  table: typeof tableTheme;
  tooltip: typeof tooltipTheme;
  toolbar: typeof toolbarTheme;
  appBar: typeof appBarTheme;
}

export const defaultMapUiTheme: MapUiTheme = {
  global: globalStyles,
  screens: screensStyles,
  colors: colors,
  spacing: spacingStyles,
  zIndices: zIndicesStyles,
  borderRadius: borderRadiusStyles,
  boxShadow: boxShadowStyles,
  fontSize: fontSizeStyles,
  fontWeight: fontWeightStyles,
  letterSpacing: letterSpacingStyles,
  lineHeight: lineHeightStyles,
  animation: animationStyles,
  keyframes: keyframesStyles,
  alert: alertTheme,
  animations: animationTheme,
  avatar: avatarTheme,
  button: buttonTheme,
  buttonGroup: buttonGroupTheme,
  card: cardTheme,
  container: containerTheme,
  dropdown: dropdownTheme,
  form: formTheme,
  icon: iconTheme,
  link: linkTheme,
  loadingSpinner: loadingSpinnerTheme,
  modal: modalTheme,
  notifications: notificationsTheme,
  section: sectionTheme,
  slider: sliderTheme,
  rating: ratingTheme,
  stepper: stepperTheme,
  table: tableTheme,
  tooltip: tooltipTheme,
  hero: heroTheme,
  toolbar: toolbarTheme,
  appBar: appBarTheme,
};
